import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  formatLong,
  formatShortDate,
  formatEuro,
  statusLabel,
  cancelledByLabel,
  paymentLabel,
  sourceLabel,
  translateServiceLabel,
  formatBookingCustomerNotes,
  internalNotesValue,
} from "./formatters";

const brandLabel = (t, brand) => {
  if (brand === "tawan-spa") return t("site.tawanSpa");
  if (brand === "dr-hoffmann" || brand === "dr-hoffmann-massage") {
    return t("site.drHoffmann");
  }
  return brand || "";
};

const studioScheduleLabel = (t, value) => {
  if (value === "thursday") return t("calendar.bookingDetail.studioThursday");
  if (value === "weekday") return t("calendar.bookingDetail.studioWeekday");
  return value;
};

const formatTime = (date, dateLocale) =>
  new Date(date).toLocaleTimeString(dateLocale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  });

const formatServiceAddress = (addr) => {
  if (!addr) return "";
  const lines = [
    addr.companyName,
    [addr.street, [addr.postalCode, addr.city].filter(Boolean).join(" ")].filter(Boolean).join(", "),
  ].filter(Boolean);
  return lines.join("\n");
};

const DetailRow = ({ label, value, multiline = false }) => (
  <>
    <dt>{label}</dt>
    <dd className={multiline ? "team-booking-detail-multiline" : undefined}>{value}</dd>
  </>
);

const TeamBookingDetailDialog = ({
  open,
  booking,
  dateLocale,
  locale,
  t,
  showSource = false,
  showBrand = false,
  statusOptions = [],
  statusSaving = false,
  onStatusChange,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !booking) return null;

  const empty = t("calendar.bookingDetail.none");
  const customerName = [booking.customer?.firstName, booking.customer?.lastName]
    .filter(Boolean)
    .join(" ");
  const canEditStatus =
    typeof onStatusChange === "function" &&
    statusOptions.length > 0 &&
    booking.status !== "cancelled" &&
    booking.status !== "pending-email-verification";

  return createPortal(
    <div className="team-delete-dialog-root">
      <div
        className="team-delete-dialog-overlay"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="team-delete-dialog team-booking-detail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-booking-detail-title"
      >
        <div className="team-detail-header">
          <h2 id="team-booking-detail-title">{t("calendar.bookingDetail.title")}</h2>
          <button
            type="button"
            className="team-close"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>

        <dl className="team-detail-dl team-booking-detail-dl">
          <DetailRow
            label={t("bookings.col.service")}
            value={
              <>
                {translateServiceLabel(t, booking)}
                {booking.isFreeFirstMassagesessel && (
                  <span className="badge" style={{ marginLeft: "0.35rem" }}>
                    {t("bookings.badge.freeTawan")}
                  </span>
                )}
              </>
            }
          />
          <DetailRow
            label={t("calendar.bookingDetail.start")}
            value={formatLong(booking.start, dateLocale)}
          />
          <DetailRow
            label={t("calendar.bookingDetail.end")}
            value={formatTime(booking.end, dateLocale)}
          />
          <DetailRow
            label={t("calendar.bookingDetail.duration")}
            value={`${booking.durationMinutes} ${t("common.minutes")}`}
          />
          <DetailRow
            label={t("bookings.col.customer")}
            value={customerName || empty}
          />
          <DetailRow
            label={t("customers.field.email")}
            value={
              booking.customer?.email ? (
                <a href={`mailto:${booking.customer.email}`}>{booking.customer.email}</a>
              ) : (
                empty
              )
            }
          />
          <DetailRow
            label={t("customers.field.phone")}
            value={
              booking.customer?.phone ? (
                <a href={`tel:${booking.customer.phone}`}>{booking.customer.phone}</a>
              ) : (
                empty
              )
            }
          />
          <DetailRow
            label={t("bookings.col.price")}
            value={
              booking.priceCents === 0
                ? t("common.free")
                : formatEuro(booking.priceCents, locale)
            }
          />
          <DetailRow label={t("bookings.col.payment")} value={paymentLabel(booking)} />
          <DetailRow
            label={t("bookings.col.status")}
            value={
              canEditStatus ? (
                <select
                  value={booking.status}
                  disabled={statusSaving}
                  onChange={(e) => onStatusChange(booking, e.target.value)}
                  className="team-inline-select team-booking-detail-status"
                >
                  {statusOptions.map((val) => (
                    <option key={val} value={val}>
                      {statusLabel(t, val)}
                    </option>
                  ))}
                </select>
              ) : booking.status === "cancelled"
                ? [
                    statusLabel(t, booking.status),
                    cancelledByLabel(
                      t,
                      booking.cancelledBy,
                      booking.staffCancellationReason
                    ),
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : statusLabel(t, booking.status)
            }
          />
          {booking.status === "pending-email-verification" ? (
            <DetailRow
              label={t("calendar.bookingDetail.hint")}
              value={t("calendar.bookingDetail.pendingEmailVerification")}
              multiline
            />
          ) : null}
          {booking.status === "cancelled" && booking.cancelledAt ? (
            <DetailRow
              label={t("calendar.bookingDetail.cancelledAt")}
              value={formatLong(booking.cancelledAt, dateLocale)}
            />
          ) : null}
          {booking.location ? (
            <DetailRow label={t("calendar.bookingDetail.location")} value={booking.location} />
          ) : null}
          {showBrand && booking.brand ? (
            <DetailRow label={t("calendar.bookingDetail.brand")} value={brandLabel(t, booking.brand)} />
          ) : null}
          {showSource && booking.source ? (
            <DetailRow
              label={t("customers.field.source")}
              value={sourceLabel(t, booking.source)}
            />
          ) : null}
          {booking.studioSchedule ? (
            <DetailRow
              label={t("calendar.bookingDetail.studioSchedule")}
              value={studioScheduleLabel(t, booking.studioSchedule)}
            />
          ) : null}
          {booking.hoffmannBookingOption ? (
            <DetailRow
              label={t("calendar.bookingDetail.option")}
              value={booking.hoffmannBookingOption}
            />
          ) : null}
          {booking.serviceAddress ? (
            <DetailRow
              label={t("calendar.bookingDetail.serviceAddress")}
              value={formatServiceAddress(booking.serviceAddress) || empty}
              multiline
            />
          ) : null}
          {booking.serviceAddress?.notes?.trim() ? (
            <DetailRow
              label={t("calendar.bookingDetail.addressNotes")}
              value={booking.serviceAddress.notes}
              multiline
            />
          ) : null}
          <DetailRow
            label={t("calendar.bookingDetail.customerNotes")}
            value={formatBookingCustomerNotes(booking) || empty}
            multiline
          />
          <DetailRow
            label={t("bookings.col.notes")}
            value={internalNotesValue(booking)?.trim() ? internalNotesValue(booking) : empty}
            multiline
          />
          {booking.staffCancellationReason ? (
            <DetailRow
              label={t("calendar.bookingDetail.cancellationReason")}
              value={booking.staffCancellationReason}
            />
          ) : null}
          <DetailRow
            label={t("calendar.bookingDetail.created")}
            value={formatShortDate(booking.createdAt, dateLocale)}
          />
        </dl>

        <div className="team-delete-dialog-actions">
          <button type="button" className="team-dialog-cancel" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TeamBookingDetailDialog;
